/**
 * 시간은 주입한다 — 테스트에서 가상 시계로 바꿔 끼우기 위해서다.
 * 실제 시계는 performance.now() 를 쓴다(벽시계 보정에 흔들리지 않는다).
 */

export interface Clock {
  now(): number;
  /** 취소 함수를 돌려준다. */
  setTimer(ms: number, fn: () => void): () => void;
}

export const realClock: Clock = {
  now: () => performance.now(),
  setTimer(ms, fn) {
    const handle = setTimeout(fn, ms);
    return () => clearTimeout(handle);
  },
};

/**
 * 깨울 수 있는 대기. 정지 요청이 들어오면 다음 tick 을 기다리지 않고 깨운다.
 * 한 번에 하나의 대기만 허용한다 — 송신 루프는 하나뿐이기 때문이다.
 */
export class Waiter {
  private cancelTimer: (() => void) | null = null;
  private resolve: (() => void) | null = null;

  constructor(private readonly clock: Clock) {}

  wait(ms: number): Promise<void> {
    if (this.resolve) throw new Error("이미 대기 중입니다");
    if (ms <= 0) return Promise.resolve();
    return new Promise<void>((resolve) => {
      this.resolve = resolve;
      this.cancelTimer = this.clock.setTimer(ms, () => this.finish());
    });
  }

  /** 대기 중이면 즉시 깨운다. 아니면 아무 일도 하지 않는다. */
  wake(): void {
    this.finish();
  }

  private finish(): void {
    const resolve = this.resolve;
    const cancel = this.cancelTimer;
    this.resolve = null;
    this.cancelTimer = null;
    if (cancel) cancel();
    if (resolve) resolve();
  }
}

export interface TimeoutResult<T> {
  timedOut: boolean;
  value?: T;
}

/**
 * 제한 시간 안에 끝나는지 본다.
 *
 * **주의 — timeout 이 됐다고 원래 작업이 취소되는 것은 아니다.** 늦게 끝난
 * Promise 가 상태를 바꾸지 못하게 하는 일은 부르는 쪽 책임이다.
 */
export function withTimeout<T>(
  clock: Clock,
  promise: Promise<T>,
  ms: number,
  onLate?: (settled: { ok: boolean; value?: T; error?: unknown }) => void,
): Promise<TimeoutResult<T>> {
  return new Promise<TimeoutResult<T>>((resolve, reject) => {
    let settled = false;
    const cancel = clock.setTimer(ms, () => {
      if (settled) return;
      settled = true;
      resolve({ timedOut: true });
    });
    promise.then(
      (value) => {
        if (settled) {
          onLate?.({ ok: true, value });
          return;
        }
        settled = true;
        cancel();
        resolve({ timedOut: false, value });
      },
      (error) => {
        if (settled) {
          onLate?.({ ok: false, error });
          return;
        }
        settled = true;
        cancel();
        reject(error);
      },
    );
  });
}
